import * as THREE from "../vendor/three/build/three.module.js";
import { InitializeCustomElements } from "./custom-elements.js";
import { SetupEnablerService, SetupExportCounter } from "./enabler-service.js";
import { CreateEngineWorker } from "./engine/worker-interface.js";
import { CreateButton, CreateCheckbox, CreateHorizontalSeparator, CreateSliderWithInput } from "./input-ui-elements.js";
import { InitializeMaterialsUI } from "./material-ui.js";
import { RequestSceneRender, geometry, material } from "./scene.js";
import { InitializeCollapsingSections } from "./ui-util.js";
import { CreateThrottler, DoWhileLoading, UpdateGeometryFromMeshData } from "./util.js";

InitializeCustomElements();
InitializeCollapsingSections();
InitializeMaterialsUI();

const exportCounter = SetupExportCounter();

const workerUrl = "./dist/engine/worker/engine-worker.js";
const engineModuleUrl = "../../../wasm";
const engineDataFileUrl = "../../../wasm/data.asar";

const engine = CreateEngineWorker(workerUrl, engineModuleUrl, engineDataFileUrl);
const enablerServiceSetup = SetupEnablerService(engine);

const loadingOverlay = document.getElementById("loading-overlay")!;

function HideLoadingOverlay()
{
    loadingOverlay.classList.add("hidden");
}

const heightMapContainer = document.getElementById("heightmap-container")!;

const importMeshButton = CreateButton({
    label: "Import"
});

const importTextureButton = CreateButton({
    label: "Import"
});

const resolutionSlider = CreateSliderWithInput({
    label: "Mesh resolution",
    minValue: 5,
    maxValue: 8,
    step: 0.001,
    defaultValue: 7
});

const textureScaleSlider = CreateSliderWithInput({
    label: "Texture scale",
    minValue: 0.5,
    maxValue: 30,
    step: 0.001,
    defaultValue: 2
});

const textureOffsetSlider = CreateSliderWithInput({
    label: "Texture offset",
    minValue: 0,
    maxValue: 1,
    step: 0.001,
    defaultValue: 0
});

const blendExponentSlider = CreateSliderWithInput({
    label: "Blend sharpness",
    minValue: 1,
    maxValue: 30,
    step: 0.001,
    defaultValue: 30
});

const blendOffsetSlider = CreateSliderWithInput({
    label: "Blend offset",
    minValue: 0,
    maxValue: 1,
    step: 0.001,
    defaultValue: 0
});

const blendHeightStrengthSlider = CreateSliderWithInput({
    label: "Blend height strength",
    minValue: 0,
    maxValue: 0.99,
    step: 0.001,
    defaultValue: 0
});

const displacementSlider = CreateSliderWithInput({
    label: "Displacement",
    minValue: 0,
    maxValue: 0.2,
    step: 0.001,
    defaultValue: 0.025
});

const invertCheckbox = CreateCheckbox({
    label: "Invert direction"
});

const generateButton = CreateButton({
    label: "Generate"
});

const autoGenerateCheckbox = CreateCheckbox({
    label: "Auto update",
    checkedByDefault: true
});

document.getElementById("texture-import-header")!.appendChild(importTextureButton.element);
document.getElementById("mesh-import-header")!.appendChild(importMeshButton.element);

heightMapContainer.appendChild(resolutionSlider.element);

heightMapContainer.appendChild(textureOffsetSlider.element);
heightMapContainer.appendChild(textureScaleSlider.element);

heightMapContainer.appendChild(textureOffsetSlider.element);
heightMapContainer.appendChild(textureScaleSlider.element);

heightMapContainer.appendChild(blendExponentSlider.element);
heightMapContainer.appendChild(blendOffsetSlider.element);
heightMapContainer.appendChild(blendHeightStrengthSlider.element);

heightMapContainer.appendChild(displacementSlider.element);
heightMapContainer.appendChild(invertCheckbox.element);

heightMapContainer.appendChild(CreateHorizontalSeparator().element);

heightMapContainer.appendChild(autoGenerateCheckbox.element);
heightMapContainer.appendChild(generateButton.element);

interface LoadedMesh
{
    name: string;
    nativePtr: Ptr;
    uiElement: HTMLElement | null;
}

const builtinHeightMapBaseUrl = "assets/heightmaps-webp/";
const builtinHeightMapPreviewUrl = "assets/heightmaps-preview/";

function GetTextureUrl(name: string, isPreview: boolean)
{
    return (isPreview ? builtinHeightMapPreviewUrl : builtinHeightMapBaseUrl) + name + ".webp";
}

interface LoadedHeightMap
{
    name: string;
    canUnload: boolean;
    texture: THREE.Texture | null;
    preview: HTMLCanvasElement | HTMLImageElement;
    uiElement: HTMLElement | null;
}

const fileSelector = document.createElement("input");
fileSelector.type = "file";
fileSelector.multiple = true;
document.body.appendChild(fileSelector);
fileSelector.style.display = "none";

interface ImportedFile
{
    name: string;
    contents: Uint8Array;
}

async function FilesToBytes(files: FileList)
{
    const fileLoadPromises: Promise<ImportedFile | null>[] = [];

    for (const file of files)
    {
        const reader = new FileReader();

        fileLoadPromises.push(new Promise<ImportedFile | null>(res =>
        {
            reader.onload = () =>
            {
                if (reader.result instanceof ArrayBuffer)
                {
                    res({
                        name: file.name,
                        contents: new Uint8Array(reader.result)
                    });
                }
                else
                {
                    res(null);
                }
            };

            reader.onerror = () => res(null);

            reader.readAsArrayBuffer(file);
        }));
    }

    const importedFiles = await Promise.all(fileLoadPromises);

    const resultFiles = [];
    for (const file of importedFiles)
    {
        if (file !== null)
        {
            resultFiles.push(file);
        }
    }

    return resultFiles;
}

async function SelectAndLoadFiles(...extensions: string[])
{
    fileSelector.value = null!;
    fileSelector.accept = extensions.map(ext => ext.startsWith(".") ? ext : ("." + ext)).join(",");

    const files = await new Promise<FileList | null>(res =>
    {
        fileSelector.onchange = () => res(fileSelector.files);
        fileSelector.click();
    })

    if (files === null)
    {
        return null;
    }

    return await DoWhileLoading(FilesToBytes(files));
}

const meshContainer = document.getElementById("loaded-meshes")!;
let selectedMesh: LoadedMesh | null = null;

const textureContainer = document.getElementById("loaded-textures")!;
let selectedHeightMap: LoadedHeightMap | null = null;

const autoUnloadTextures = false;

function SelectMesh(mesh: LoadedMesh)
{
    if (selectedMesh === mesh)
    {
        return;
    }

    selectedMesh?.uiElement?.classList.remove("selected");
    selectedMesh = mesh;
    selectedMesh.uiElement?.classList.add("selected");

    meshGeometryNeedsUpdate = true;

    AutoGenerateMeshIfNeeded();
}

async function SelectTexture(heightMap: LoadedHeightMap)
{
    if (selectedHeightMap === heightMap)
    {
        return;
    }

    if (selectedHeightMap !== null &&
        autoUnloadTextures &&
        selectedHeightMap.canUnload &&
        selectedHeightMap.texture !== null)
    {
        selectedHeightMap.texture.dispose();
        selectedHeightMap.texture = null;
    }

    selectedHeightMap?.uiElement?.classList.remove("selected");
    selectedHeightMap = heightMap;
    selectedHeightMap.uiElement?.classList.add("selected");

    if (heightMap.texture === null)
    {
        console.assert(heightMap.canUnload);

        // Load and create texture
        const textureUrl = GetTextureUrl(heightMap.name, false);
        heightMap.texture = await DoWhileLoading(LoadTextureFromUrl(textureUrl));
    }

    material.shader!.uniforms.heightMap.value = heightMap.texture;
    material.shader!.uniforms.heightMapAspect.value = heightMap.texture.image.width / heightMap.texture.image.height;

    AutoGenerateMeshIfNeeded();
}

function AddMeshesToList(meshes: LoadedMesh[], autoSelect: boolean)
{
    for (const mesh of meshes)
    {
        const elem = document.createElement("div");
        elem.textContent = mesh.name;
        meshContainer.appendChild(elem);

        mesh.uiElement = elem;

        if (selectedMesh === null)
        {
            SelectMesh(mesh);
        }

        elem.addEventListener("click", () => SelectMesh(mesh));
    }

    if (autoSelect && meshes.length !== 0)
    {
        SelectMesh(meshes[0]);
    }
}

function AddTexturesToList(textures: LoadedHeightMap[], autoSelect: boolean)
{
    for (const texture of textures)
    {
        const elem = document.createElement("div");
        const name = document.createElement("div");

        name.textContent = texture.name;

        elem.appendChild(texture.preview);
        elem.appendChild(name);

        textureContainer.appendChild(elem);

        texture.uiElement = elem;

        if (selectedHeightMap === null)
        {
            SelectTexture(texture);
        }

        elem.addEventListener("click", () => SelectTexture(texture));
    }

    if (autoSelect && textures.length !== 0)
    {
        SelectTexture(textures[0]);
    }
}

async function LoadMeshFromBytes(file: ImportedFile): Promise<LoadedMesh>
{
    const nativePtr = await engine.ImportMesh(file.name.toLowerCase().endsWith(".stl") ? "stl" : "obj", file.contents);

    return {
        name: file.name,
        nativePtr,
        uiElement: null
    };
}

async function ImportMeshFiles()
{
    const loadedFiles = await SelectAndLoadFiles("obj", "stl");
    if (loadedFiles === null)
    {
        return;
    }

    const newMeshes = loadedFiles.map(LoadMeshFromBytes);
    AddMeshesToList(await DoWhileLoading(Promise.all(newMeshes)), true);
}

async function LoadTextureFromUrl(url: string)
{
    const texture = await new THREE.TextureLoader().loadAsync(url)

    texture.format = THREE.RedFormat; // Only one channel is needed
    texture.generateMipmaps = false;
    texture.wrapS = THREE.RepeatWrapping; // Must repeat for triplanar effect
    texture.wrapT = THREE.RepeatWrapping;
    texture.needsUpdate = true;

    return texture;
}

async function LoadTextureFromBytes(file: ImportedFile): Promise<LoadedHeightMap>
{
    function GetMimeType(fileName: string)
    {
        fileName = fileName.toLowerCase();
        if (fileName.endsWith(".png"))
        {
            return "image/png";
        }
        else if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
        {
            return "image/jpeg";
        }
        else if (fileName.endsWith("bmp"))
        {
            return "image/bmp";
        }

        return undefined;
    }

    const originalImage = new Image();
    const url = URL.createObjectURL(new Blob([file.contents], { type: GetMimeType(file.name) }));
    originalImage.src = url;

    const [texture, preview] = await Promise.all([
        LoadTextureFromUrl(url),
        new Promise<HTMLCanvasElement>(res =>
        {
            originalImage.addEventListener("load", () =>
            {
                const targetWidth = 256;
                const aspect = originalImage.width / originalImage.height;
                const targetHeight = Math.round(targetWidth / aspect);

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d")!;

                canvas.width = targetWidth;
                canvas.height = targetHeight;

                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(originalImage, 0, 0, targetWidth, targetHeight);
                res(canvas);
            });
        })
    ]);

    return {
        name: file.name,
        canUnload: false,
        texture,
        preview,
        uiElement: null
    };
}

async function ImportTextureFiles()
{
    const loadedFiles = await SelectAndLoadFiles("png", "jpg", "jpeg", "bmp");
    if (loadedFiles === null)
    {
        return;
    }

    const newTextures = loadedFiles.map(LoadTextureFromBytes);
    AddTexturesToList(await DoWhileLoading(Promise.all(newTextures)), true);
}

importMeshButton.onClick.addListener(ImportMeshFiles);
importTextureButton.onClick.addListener(ImportTextureFiles);

let meshGeometryNeedsUpdate = true;
resolutionSlider.onChange.addListener(() => meshGeometryNeedsUpdate = true);

const vertexCountText = document.getElementById("vertex-count")!;
const triangleCountText = document.getElementById("triangle-count")!;

function FormatNumberWithThousands(num: number)
{
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

const GenerateMesh = CreateThrottler(async () =>
{
    if (selectedMesh === null)
    {
        return;
    }

    await enablerServiceSetup;

    if (meshGeometryNeedsUpdate)
    {
        meshGeometryNeedsUpdate = false;
        const mesh = selectedMesh;

        await DoWhileLoading(async () =>
        {
            const meshData = await engine.GenerateMesh(mesh.nativePtr, 0, {
                meshResolution: resolutionSlider.getValue(),
                displacement: 0,
                invertImage: false,
                textureOffset: { x: 0, y: 0, z: 0 },
                textureScale: 1,
                triplanarBlendExponent: 1,
                triplanarBlendHeightStrength: 0,
                triplanarBlendOffset: 0
            });

            UpdateGeometryFromMeshData(geometry, meshData);
            vertexCountText.textContent = FormatNumberWithThousands(meshData.vertices.length / 3);
            triangleCountText.textContent = FormatNumberWithThousands(meshData.triangles.length / 3);
        });
    }

    material.shader!.uniforms.invTextureScale.value = 1 / textureScaleSlider.getValue();
    material.shader!.uniforms.textureOffset.value.setScalar(textureOffsetSlider.getValue());
    material.shader!.uniforms.triplanarBlendExponent.value = blendExponentSlider.getValue();
    material.shader!.uniforms.triplanarBlendOffset.value = blendOffsetSlider.getValue();
    material.shader!.uniforms.triplanarBlendHeightStrength.value = blendHeightStrengthSlider.getValue();
    material.shader!.uniforms.displacement.value = displacementSlider.getValue();

    const invertImage = invertCheckbox.getValue();
    material.shader!.uniforms.heightMul.value = invertImage ? -1 : 1;
    material.shader!.uniforms.heightAdd.value = invertImage ? 1 : 0;

    // heightMap is updated in SelectTexture

    material.needsUpdate = true;
    RequestSceneRender();

    exportCounter.dispatchEvent();
});

function AutoGenerateMeshIfNeeded()
{
    if (autoGenerateCheckbox.getValue())
    {
        GenerateMesh();
    }
}

generateButton.onClick.addListener(GenerateMesh);

const elements = [
    resolutionSlider,
    displacementSlider,
    invertCheckbox,
    textureOffsetSlider,
    textureScaleSlider,
    blendExponentSlider,
    blendHeightStrengthSlider,
    blendOffsetSlider,
    autoGenerateCheckbox
];

for (const elem of elements)
{
    elem.onChange.addListener(AutoGenerateMeshIfNeeded);
}

// Preload meshes and the first texture, then load the rest of the textures

const meshPreloadPromises: Promise<LoadedMesh>[] = [];

const meshesToPreload = [
    "icosphere.obj",
    "cube.obj"
];

const texturesToPreload = [
    "HP-0001",
    "HP-0002",
    "HP-0003",
    "HP-0004",
    "HP-0005",
    "HP-0006",
    "HP-0007",
    "HP-0008",
    "HP-0009",
    "HP-0011",
    "HP-0012",
    "HP-0013",
    "HP-0014",
    "HP-0015",
    "HP-0016",
    "HP-0017",
    "HP-0018",
    "HP-0019",
    "HP-0020",
    "HP-0022",
    "HP-0023",
    "HP-0024",
    "HP-0025",
    "HP-0028",
    "HP-0030",
    "HP-0031",
    "HP-0032",
    "HP-0033",
    "HP-0034",
    "HP-0035",
    "HP-0036",
    "HP-0037",
    "HP-0038",
    "HP-0039",
    "HP-0040",
    "HP-0041",
    "HP-0042",
    "HP-0043",
    "HP-0044",
    "HP-0045",
    "HP-0047",
    "HP-0048",
    "HP-0049",
    "HP-0050",
    "HP-0051",
    "HP-0052",
    "HP-0054",
    "HP-0055",
    "HP-0056",
    "HP-0057",
    "HP-0059",
    "HP-0061",
    "HP-0062",
    "HP-0063",
    "HP-0064",
    "HP-0065",
    "HP-0067",
    "HP-0068",
    "HP-0069",
    "HP-0070",
    "HP-0071",
    "HP-0073",
    "HP-0074",
    "HP-0075",
    "HP-0076",
    "HP-0078",
    "HP-0080",
    "HP-0081",
    "HP-0082",
    "HP-0083"
];

for (const meshPath of meshesToPreload)
{
    async function PreloadMesh()
    {
        const fullPath = "assets/meshes/" + meshPath;
        const req = await fetch(fullPath);
        const buf = await req.arrayBuffer();

        return await LoadMeshFromBytes({ name: meshPath, contents: new Uint8Array(buf) });
    }

    meshPreloadPromises.push(PreloadMesh());
}

Promise.all(meshPreloadPromises).then(meshes =>
{
    AddMeshesToList(meshes, false);
    HideLoadingOverlay();

    // Load texture previews
    const heightMapElements: LoadedHeightMap[] = [];
    for (const textureName of texturesToPreload)
    {
        const path = GetTextureUrl(textureName, true);
        const img = new Image();
        img.loading = "lazy";
        img.src = path;

        heightMapElements.push({
            name: textureName,
            canUnload: true,
            preview: img,
            texture: null,
            uiElement: null
        });
    }

    AddTexturesToList(heightMapElements, false);
});
